import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import styles from '../../styles/AdminDashboard.module.css';
import {
  FaStar,
  FaEllipsisV,
  FaUserCircle,
  FaSearch,
  FaArrowLeft,
  FaArrowRight,
} from 'react-icons/fa';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        const userData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(userData);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  const calculateRating = (reviews = []) => {
    if (!reviews.length) return 0;
    const total = reviews.reduce((sum, r) => sum + (r.rating || 0), 0);
    return total / reviews.length;
  };

  const filteredUsers = users.filter((user) =>
    (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className={styles.dashboardWrapper}>
      <h2 className={styles.dashboardTitle}>Admin Dashboard</h2>

      {/* Search Bar structure remains the same */}
      <div className={styles.searchBar}>
        <FaSearch className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.userTable}>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Profile</th>
              <th>User ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Rating</th>
              <th>Menu</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user, idx) => {
              const avgRating = calculateRating(user.reviews || []);
              return (
                <tr key={user.id}>
                  <td>{indexOfFirstUser + idx + 1}</td>
                  <td>
                    {user.profileImage ? (
                      <img
                        src={user.profileImage}
                        alt="Profile"
                        className={styles.profileImg}
                      />
                    ) : (
                      <FaUserCircle size={32} className={styles.fallbackIcon} />
                    )}
                  </td>
                  <td>{user.id}</td>
                  <td>{user.name || '—'}</td>
                  <td>{user.email || '—'}</td>
                  <td>
                    <div className={styles.rating}>
                      {[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          className={i < avgRating ? styles.filledStar : styles.emptyStar}
                        />
                      ))}
                    </div>
                  </td>
                  <td>
                    <FaEllipsisV className={styles.menuIcon} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              className={styles.pageArrow}
              disabled={currentPage === 1}
            >
              <FaArrowLeft />
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => handlePageChange(i + 1)}
                className={`${styles.pageButton} ${
                  currentPage === i + 1 ? styles.activePage : ''
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              className={styles.pageArrow}
              disabled={currentPage === totalPages}
            >
              <FaArrowRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;